<template>
  <div ref="trigger" class="relative inline-flex" @mouseenter="onEnter" @mouseleave="onLeave">
    <slot />
  </div>

  <teleport to="body">
    <transition name="surface-editor-tooltip">
      <div
        v-if="open && hasContent"
        ref="tooltip"
        class="fixed z-[99999] pointer-events-none"
        :style="tooltipStyle"
      >
        <div class="relative">
          <div
            class="absolute w-2 h-2 bg-charcoal-600 rotate-45 -translate-x-1/2"
            :class="arrowClass"
            :style="arrowStyle"
          />
          <div class="rounded bg-charcoal-600 text-white text-sm py-1 px-2.5 flex flex-col items-center gap-1 font-semibold shadow">
            <slot name="text">
              <span v-if="text">{{ text }}</span>
              <span v-if="shortcutParts.length" class="flex items-center gap-0.5 p-1.5 rounded bg-charcoal-700 border border-white/10 text-2xs leading-none">
                <span
                  v-for="(shortcut, index) in shortcutParts"
                  :key="`${shortcut}-${index}`"
                  class="flex items-center gap-1"
                >
                  <span class="">
                    {{ shortcut }}
                  </span>
                  <span v-if="index < shortcutParts.length - 1" class="text-white/70">+</span>
                </span>
              </span>
            </slot>
          </div>
        </div>
      </div>
    </transition>
  </teleport>
</template>

<script setup>
  const props = defineProps({
    text: {
      type: String,
      default: ''
    },
    popper: {
      type: Object,
      default: null
    },
    placement: {
      type: String,
      default: 'top'
    },
    offset: {
      type: Number,
      default: 8
    },
    openDelay: {
      type: Number,
      default: 100
    },
    closeDelay: {
      type: Number,
      default: 50
    },
    shortcuts: {
      type: Array,
      default: () => []
    },
    prevent: {
      type: Boolean,
      default: false
    }
  });

  const trigger = ref(null);
  const tooltip = ref(null);
  const open = ref(false);
  const tooltipStyle = ref({});
  const arrowStyle = ref({});
  const resolvedSide = ref('top');
  const resolvedPlacement = computed(() => props.popper?.placement || props.placement);
  const resolvedOffset = computed(() => props.popper?.offsetDistance ?? props.offset);
  const shortcutParts = computed(() => {
    if (Array.isArray(props.shortcuts)) return props.shortcuts.filter(Boolean);
    return props.shortcuts ? [props.shortcuts] : [];
  });
  const hasContent = computed(() => Boolean(props.text || shortcutParts.value.length));
  let openTimeout = null;
  let closeTimeout = null;

  const arrowClass = computed(() => {
    const side = resolvedSide.value;
    return side === 'bottom' ? '-top-1' : '-bottom-1';
  });

  function computePosition() {
    if (!trigger.value || !tooltip.value) return;

    const rect = trigger.value.getBoundingClientRect();
    const tipRect = tooltip.value.getBoundingClientRect();
    const tipWidth = tipRect.width;
    const tipHeight = tipRect.height;
    const [side, align] = resolvedPlacement.value.split('-');
    const margin = 8;
    const viewportWidth = window.innerWidth;
    const viewportHeight = window.innerHeight;

    const clamp = (value, min, max) => Math.min(Math.max(value, min), max);
    const getHorizontal = () => {
      if (align === 'start') return rect.left;
      if (align === 'end') return rect.right - tipWidth;
      return rect.left + (rect.width - tipWidth) / 2;
    };

    const rawLeft = getHorizontal();
    const maxLeft = Math.max(margin, viewportWidth - tipWidth - margin);
    const left = clamp(rawLeft, margin, maxLeft);

    const topY = rect.top - resolvedOffset.value - tipHeight;
    const bottomY = rect.bottom + resolvedOffset.value;

    let resolved = side === 'bottom' ? 'bottom' : 'top';
    if (resolved === 'top' && topY < margin && bottomY + tipHeight <= viewportHeight - margin) {
      resolved = 'bottom';
    } else if (resolved === 'bottom' && bottomY + tipHeight > viewportHeight - margin && topY >= margin) {
      resolved = 'top';
    }

    let top = resolved === 'bottom' ? bottomY : topY;
    const maxTop = Math.max(margin, viewportHeight - tipHeight - margin);
    top = clamp(top, margin, maxTop);

    const triggerCenterX = rect.left + rect.width / 2;
    const arrowLeft = clamp(triggerCenterX - left, 8, tipWidth - 8);

    resolvedSide.value = resolved;
    tooltipStyle.value = {
      left: `${Math.round(left)}px`,
      top: `${Math.round(top)}px`
    };

    arrowStyle.value = {
      left: `${Math.round(arrowLeft)}px`
    };
  }

  function addListeners() {
    window.addEventListener('scroll', computePosition, true);
    window.addEventListener('resize', computePosition);
  }

  function removeListeners() {
    window.removeEventListener('scroll', computePosition, true);
    window.removeEventListener('resize', computePosition);
  }

  function onEnter() {
    if (props.prevent) return;
    if (closeTimeout) {
      clearTimeout(closeTimeout);
      closeTimeout = null;
    }

    openTimeout = openTimeout || setTimeout(() => {
      open.value = true;
      nextTick(() => {
        computePosition();
        addListeners();
      });
      openTimeout = null;
    }, props.openDelay);
  }

  function onLeave() {
    if (openTimeout) {
      clearTimeout(openTimeout);
      openTimeout = null;
    }

    closeTimeout = closeTimeout || setTimeout(() => {
      open.value = false;
      removeListeners();
      closeTimeout = null;
    }, props.closeDelay);
  }

  onBeforeUnmount(() => {
    removeListeners();
    if (openTimeout) clearTimeout(openTimeout);
    if (closeTimeout) clearTimeout(closeTimeout);
  });

  watch(
    () => props.prevent,
    (value) => {
      if (!value) return;
      if (openTimeout) {
        clearTimeout(openTimeout);
        openTimeout = null;
      }
      if (closeTimeout) {
        clearTimeout(closeTimeout);
        closeTimeout = null;
      }
      open.value = false;
      removeListeners();
    }
  );
</script>

<style scoped>
  .surface-editor-tooltip-enter-active,
  .surface-editor-tooltip-leave-active {
    transition: opacity 120ms ease, transform 120ms ease;
  }

  .surface-editor-tooltip-enter-from,
  .surface-editor-tooltip-leave-to {
    opacity: 0;
    transform: translateY(2px);
  }
</style>
